import { createCheckoutPayment as createCheckoutPaymentAction } from 'actions/entities/payments/createCheckoutPayment';
import { INVALID_PARAM_ERROR } from 'constants/errors';
import { HTTPStatuses, type HttpError } from 'constants/http';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'store/configureStore';

type UsePaymentCreationOptions = {
  skipPaymentCreation?: boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type UsePaymentCreationReturn = {
  createCheckoutPayment: () => Promise<void>;
  isCreating: boolean;
  error: Error | null;
  resetError: () => void;
};

export const usePaymentCreation = ({
  skipPaymentCreation = false,
  onSuccess,
  onError,
}: UsePaymentCreationOptions = {}): UsePaymentCreationReturn => {
  const dispatch = useDispatch();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  const createCheckoutPayment = useCallback(async () => {
    setIsCreating(true);
    setError(null);

    try {
      await dispatch(
        createCheckoutPaymentAction({
          skipPayment: skipPaymentCreation,
        }),
      );
      onSuccessRef.current?.();
    } catch (e) {
      const error = e as HttpError;
      const { parsedBody: { errors: validationErrors = [] } = {} } = error;

      if (
        error.status !== HTTPStatuses.UNPROCESSABLE_ENTITY ||
        !validationErrors.some(error => error.type === INVALID_PARAM_ERROR)
      ) {
        const normalizedError = e instanceof Error ? e : new Error(String(e));
        setError(normalizedError);
        onErrorRef.current?.(normalizedError);
      }
    } finally {
      setIsCreating(false);
    }
  }, [dispatch, skipPaymentCreation]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    createCheckoutPayment,
    isCreating,
    error,
    resetError,
  };
};
